import type { RefObject } from "react";

import type { TimelineSpan } from "@/data/join-chapters";
import type { FeatureFlags } from "@/config/features";
import type { JourneyMode } from "@/data/schema/compiled";
import type { ChapterBoard } from "@/lib/timeline/board";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";
import type { TimelineUiPreferences } from "@/lib/timeline/view/TimelineUiPreferences/prefs";
import { BookTimelineMarkers } from "./BookTimelineMarkers";
import { BookTimelineStageChrome } from "./BookTimelineStageChrome";
import { useFadingControls } from "./useFadingControls";
import { useScreenAwake } from "./useScreenAwake";
import { useStageSwipe } from "./useStageSwipe";
import { useTickClickSeek } from "./useTickClickSeek";
import type { useBookTimelineStage } from "./useBookTimelineStage";

type StageApi = ReturnType<typeof useBookTimelineStage>;
import { TimelineSpinner } from "./TimelineSpinner";

interface BookTimelineViewStageProps {
  stage: StageApi;
  stageClassName: string;
  canDraw: boolean;
  canvasEl: HTMLCanvasElement | null;
  setCanvasEl: (node: HTMLCanvasElement | null) => void;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  layout: BookTimelineLayout;
  chapterBoard?: ChapterBoard;
  boardDebugActive: boolean;
  getPlayback: () => PlaybackState;
  uiPrefs: TimelineUiPreferences;
  updateUiPrefs: (patch: Partial<TimelineUiPreferences>) => void;
  playback: {
    transport: { playing: boolean; speed: number };
    togglePlay: () => void;
    restart: () => void;
    repeat: boolean;
    toggleRepeat: () => void;
    setScrub: (direction: import("../../lib/timeline/playback").ScrubDirection) => void;
    skipScene: (direction: -1 | 1) => void;
    seekTo: (playheadMs: number) => void;
    setSpeed: (speed: number) => void;
  };
  spans: TimelineSpan[];
  spanId: string;
  onSpanIdChange: (id: string) => void;
  featureFlags: FeatureFlags;
  journey: JourneyMode;
  journeyLoading: boolean;
  journeyFailed: boolean;
  onJourneyChange: (journey: JourneyMode) => void;
  onJourneyPrefetch: () => void;
}

export function BookTimelineViewStage({
  stage,
  stageClassName,
  canDraw,
  canvasEl,
  setCanvasEl,
  canvasRef,
  layout,
  chapterBoard,
  boardDebugActive,
  getPlayback,
  uiPrefs,
  updateUiPrefs,
  playback,
  spans,
  spanId,
  onSpanIdChange,
  featureFlags,
  journey,
  journeyLoading,
  journeyFailed,
  onJourneyChange,
  onJourneyPrefetch,
}: BookTimelineViewStageProps) {
  const onCameraLockToggle = (): void => {
    updateUiPrefs(uiPrefs.cameraLockEnabled
      ? { cameraLockEnabled: false, autoZoomEnabled: false }
      : { cameraLockEnabled: true });
  };
  const onAutoZoomToggle = (): void => {
    updateUiPrefs(uiPrefs.autoZoomEnabled
      ? { autoZoomEnabled: false }
      : { cameraLockEnabled: true, autoZoomEnabled: true });
  };
  useScreenAwake(playback.transport.playing);
  useFadingControls(playback.transport.playing, stage.stageRef);
  useStageSwipe(stage.stageRef, uiPrefs.cameraLockEnabled, playback.skipScene);
  useTickClickSeek(stage, layout, playback.seekTo);

  return (
    <div className="timeline-page">
      <div className={stageClassName} ref={stage.stageRef}>
        {journeyLoading ? <TimelineSpinner label="Loading…" phrasesBookId={layout.bookId} /> : null}
        {journeyFailed ? (
          <div className="timeline-stage__notice" role="alert">
            Could not load the universe view.
          </div>
        ) : null}
        <BookTimelineMarkers
          canvas={canvasEl}
          layout={layout}
          chapterBoard={chapterBoard}
          boardModelDebug={boardDebugActive}
          getPlayback={getPlayback}
          getViewportWidth={stage.getViewportWidth}
          getViewportHeight={() => stage.viewport.height}
          getViewTransform={stage.getViewTransform}
        />
        <canvas
          ref={(node) => {
            canvasRef.current = node;
            setCanvasEl(node);
          }}
          className="timeline-canvas"
          style={{
            width: `${stage.viewport.width}px`,
            height: `${stage.viewport.height}px`,
          }}
        />
        {canDraw ? null : (
          <p className="timeline-no-graphics" role="alert">
            This browser can&apos;t draw the timeline. Try the latest Safari, Chrome, or Edge.
          </p>
        )}
        <BookTimelineStageChrome
          canvas={canvasEl}
          layout={layout}
          getPlayback={getPlayback}
          stageRef={stage.stageRef}
          uiPrefs={uiPrefs}
          updateUiPrefs={updateUiPrefs}
          playing={playback.transport.playing}
          speed={playback.transport.speed}
          onPlayPause={playback.togglePlay}
          onRestart={() => {
            playback.restart();
            if (!uiPrefs.cameraLockEnabled) {
              stage.resetToHomeKeepingZoom();
            }
          }}
          repeat={playback.repeat}
          onRepeatToggle={playback.toggleRepeat}
          onScrub={playback.setScrub}
          onSkipScene={playback.skipScene}
          onSpeedChange={playback.setSpeed}
          onCameraLockToggle={onCameraLockToggle}
          onAutoZoomToggle={onAutoZoomToggle}
          spans={spans}
          spanId={spanId}
          onSpanIdChange={onSpanIdChange}
          featureFlags={featureFlags}
          journey={journey}
          onJourneyChange={onJourneyChange}
          onJourneyPrefetch={onJourneyPrefetch}
        />
      </div>
    </div>
  );
}
